import { proxy, ref } from 'valtio'
import {
  FreeTalkApi,
  FreeTalkHistoryItem,
  FreeTalkMistake,
  FreeTalkRole,
  FreeTalkWord,
} from '../../shared/api/freeTalkApi.ts'
import { StreamService } from '../speech/audio/streamService.ts'
import { TimerId } from '../../shared/lib/utils.ts'

export type Phase = 'idle' | 'rec' | 'think' | 'speak'
export type Screen = 'talk' | 'summary'
export type MicPerm = 'unknown' | 'granted' | 'denied' | 'unsupported'
export type MessageStatus = 'ok' | 'failed' | 'unrec'

export interface Message {
  id: number
  who: 'ai' | 'me'
  kind?: 'typing' | 'error' | 'divider'
  status: MessageStatus
  text: string
  translation: string
  durationSec: number
  textOpen: boolean
  trOpen: boolean
  audioUrl?: string | null
  translating?: boolean
  /** Learner recording kept in page memory for replay and resend (ER-05). */
  blob?: Blob
  blobUrl?: string
}

export interface Summary {
  loading: boolean
  error: boolean
  short: boolean
  mistakes: FreeTalkMistake[]
  words: FreeTalkWord[]
  speechSeconds: number
  userTurns: number
}

interface State {
  screen: Screen
  phase: Phase
  started: boolean
  starting: boolean
  startError: boolean
  micPerm: MicPerm
  role: FreeTalkRole
  level: string
  userName: string
  msgs: Message[]
  playingId: number | null
  playProgress: number
  recSeconds: number
  caption: string
  captionFlash: boolean
  hintOpen: boolean
  roleOpen: boolean
  secondsToday: number
  sentToday: number
  summaryFrom: number
  summary: Summary
  vocab: string[]
  toast: string
}

export const MAX_RECORD_SEC = 60
export const WARN_RECORD_SEC = 50
export const MIN_RECORD_SEC = 1
// Dev knob: VITE_FREE_TALK_MIN_TURNS=1 shows the full summary after the first turn.
export const SHORT_TALK_TURNS =
  Number(import.meta.env.VITE_FREE_TALK_MIN_TURNS ?? 3) || 3
export const HISTORY_DEPTH = 20

export const STATUS: Record<Phase, { text: string; color: string }> = {
  idle: { text: 'Ждёт твоего ответа', color: '#ADB3FF' },
  rec: { text: 'Слушает тебя', color: '#FC723D' },
  think: { text: 'Думает', color: '#FFC861' },
  speak: { text: 'Говорит', color: '#60D974' },
}

export const CAPTION: Record<Phase, string> = {
  idle: 'Нажми на микрофон и отвечай. Ошибаться можно',
  rec: 'Нажми ещё раз, когда договоришь',
  think: 'Собеседник думает…',
  speak: 'Можно перебить: нажми на микрофон и отвечай',
}

const DEFAULT_ROLE: FreeTalkRole = {
  id: 'friend',
  name: 'Собеседник',
  description: 'Болтаем о чём угодно. Говори в своём темпе.',
  hints: [],
}

const emptySummary = (): Summary => ({
  loading: false,
  error: false,
  short: false,
  mistakes: [],
  words: [],
  speechSeconds: 0,
  userTurns: 0,
})

export class FreeTalkStore {
  state = proxy<State>({
    screen: 'talk',
    phase: 'idle',
    started: false,
    starting: false,
    startError: false,
    micPerm: 'unknown',
    role: DEFAULT_ROLE,
    level: 'A2',
    userName: '',
    msgs: [],
    playingId: null,
    playProgress: 0,
    recSeconds: 0,
    caption: CAPTION.idle,
    captionFlash: false,
    hintOpen: false,
    roleOpen: false,
    secondsToday: 0,
    sentToday: 0,
    summaryFrom: 0,
    summary: emptySummary(),
    vocab: [],
    toast: '',
  })

  private seq = 0
  private gen = 0
  private recStart = 0
  private recTimer?: TimerId
  private recCancelled = false
  private audio?: HTMLAudioElement
  private utterance?: SpeechSynthesisUtterance
  private playRaf?: number
  private flashTimer?: TimerId
  private toastTimer?: TimerId
  private summaryAbort?: AbortController
  private turnAbort?: AbortController

  constructor(
    private api: FreeTalkApi,
    private streamService: StreamService,
  ) {}

  // -- lifecycle -----------------------------------------------------------------------------------------------------

  async open() {
    this.state.screen = 'talk'
    this.state.phase = 'idle'
    this.setCaption()
    if (this.state.started) {
      return
    }
    // Old browsers (ER-10): the typings promise these, the runtime may not.
    const mediaDevices = navigator.mediaDevices as MediaDevices | undefined
    if (
      typeof mediaDevices?.getUserMedia !== 'function' ||
      typeof window.MediaRecorder === 'undefined'
    ) {
      this.state.micPerm = 'unsupported'
      return
    }
    if (this.state.micPerm !== 'granted') {
      // Permission modal is shown by the UI; the greeting waits for the mic.
      return
    }
    await this.startConversation()
  }

  async requestMic() {
    try {
      await this.streamService.getRecorder()
      this.state.micPerm = 'granted'
      await this.startConversation()
      return true
    } catch {
      this.state.micPerm = 'denied'
      return false
    }
  }

  dispose() {
    this.gen++
    this.stopRecordTimer()
    this.stopPlayback()
    this.turnAbort?.abort()
    this.summaryAbort?.abort()
    this.streamService.stopTracks()
    this.state.msgs.forEach((m) => {
      m.blobUrl && URL.revokeObjectURL(m.blobUrl)
    })
  }

  async startConversation() {
    if (this.state.started || this.state.starting) {
      return
    }
    const gen = this.gen
    this.state.starting = true
    this.state.startError = false
    this.state.msgs = []
    this.state.summaryFrom = 0
    this.state.phase = 'think'
    this.setCaption()
    this.addMsg({ who: 'ai', kind: 'typing' })
    try {
      const res = await this.api.start(this.state.role.id)
      if (gen !== this.gen) return
      this.state.role = res.role
      this.state.level = res.level
      this.state.userName = res.user_name
      this.removeTyping()
      const m = this.addMsg({
        who: 'ai',
        text: res.greeting,
        translation: res.greeting_translation,
        audioUrl: res.audio_url,
        durationSec: estimateDuration(res.greeting, res.level),
      })
      this.state.started = true
      this.state.phase = 'idle'
      this.setCaption()
      void this.play(m.id, true)
    } catch {
      if (gen !== this.gen) return
      this.removeTyping()
      this.state.startError = true
      this.state.phase = 'idle'
      this.setCaption()
    } finally {
      this.state.starting = false
    }
  }

  // -- messages ------------------------------------------------------------------------------------------------------

  private addMsg(partial: Partial<Message> & { who: Message['who'] }) {
    const m: Message = {
      id: ++this.seq,
      status: 'ok',
      text: '',
      translation: '',
      durationSec: 0,
      textOpen: false,
      trOpen: false,
      ...partial,
    }
    if (m.blob) {
      m.blob = ref(m.blob) as Blob
    }
    this.state.msgs.push(m)
    return this.state.msgs[this.state.msgs.length - 1]
  }

  private removeTyping() {
    this.state.msgs = this.state.msgs.filter((m) => m.kind !== 'typing')
  }

  private find(id: number) {
    return this.state.msgs.find((m) => m.id === id)
  }

  toggleText(id: number) {
    const m = this.find(id)
    if (!m) return
    m.textOpen = !m.textOpen
    if (!m.textOpen) m.trOpen = false
  }

  toggleTranslation(id: number) {
    const m = this.find(id)
    if (!m) return
    m.trOpen = !m.trOpen
    if (m.trOpen && !m.translation && m.text && !m.translating) {
      m.translating = true
      this.api
        .translate(m.text)
        .then((res) => {
          m.translation = res.translation || '—'
        })
        .catch(() => {
          m.translation = ''
        })
        .finally(() => {
          m.translating = false
        })
    }
  }

  /** Summary: replay the learner's own recording that contains the quoted mistake. */
  playSaid(said: string) {
    const key = normalizeSpeech(said)
    const m = [...this.state.msgs]
      .reverse()
      .find(
        (x) =>
          x.who === 'me' &&
          !x.kind &&
          x.blobUrl &&
          normalizeSpeech(x.text).includes(key),
      )
    if (m) void this.play(m.id, false)
  }

  history(): FreeTalkHistoryItem[] {
    return this.state.msgs
      .filter((m) => !m.kind && m.status === 'ok' && m.text)
      .map(
        (m) =>
          ({ role: m.who === 'ai' ? 'ai' : 'user', text: m.text }) as const,
      )
      .slice(-HISTORY_DEPTH)
  }

  // -- captions, hints, toasts ---------------------------------------------------------------------------------------

  private setCaption(text?: string, flash = false) {
    this.state.caption = text ?? CAPTION[this.state.phase]
    this.state.captionFlash = flash
  }

  flash(text: string) {
    clearTimeout(this.flashTimer)
    this.setCaption(text, true)
    this.flashTimer = setTimeout(() => {
      this.setCaption()
    }, 2600)
  }

  showToast(text: string) {
    clearTimeout(this.toastTimer)
    this.state.toast = text
    this.toastTimer = setTimeout(() => (this.state.toast = ''), 2600)
  }

  toggleHint() {
    this.state.hintOpen = !this.state.hintOpen
    this.state.roleOpen = false
  }

  closeHint() {
    this.state.hintOpen = false
  }

  toggleRoleMenu() {
    this.state.roleOpen = !this.state.roleOpen
    this.state.hintOpen = false
  }

  closeRoleMenu() {
    this.state.roleOpen = false
  }

  // -- recording -----------------------------------------------------------------------------------------------------

  micPress() {
    const phase = this.state.phase
    if (phase === 'think') return
    if (phase === 'rec') {
      void this.sendRecording(false)
      return
    }
    void this.startRecording()
  }

  async startRecording() {
    if (this.state.phase === 'rec' || this.state.phase === 'think') return
    this.stopPlayback()
    // The hint stays open while recording: the learner may be reading it.
    this.state.roleOpen = false
    clearTimeout(this.flashTimer)
    try {
      const recorder = await this.streamService.getRecorder()
      recorder.start()
    } catch {
      this.state.micPerm = 'denied'
      return
    }
    this.recCancelled = false
    this.recStart = performance.now()
    this.state.recSeconds = 0
    this.state.phase = 'rec'
    this.setCaption()
    this.stopRecordTimer()
    this.recTimer = setInterval(() => {
      this.tickRecording()
    }, 100)
  }

  private tickRecording() {
    if (this.state.phase !== 'rec') return
    const s = (performance.now() - this.recStart) / 1000
    this.state.recSeconds = s
    if (s >= WARN_RECORD_SEC && s < MAX_RECORD_SEC) {
      this.setCaption(
        `Осталось ${Math.ceil(MAX_RECORD_SEC - s)} с — потом отправлю сам`,
        true,
      )
    }
    if (s >= MAX_RECORD_SEC) {
      void this.sendRecording(true)
    }
  }

  private stopRecordTimer() {
    clearInterval(this.recTimer)
    this.recTimer = undefined
  }

  async cancelRecording() {
    if (this.state.phase !== 'rec') return
    this.recCancelled = true
    this.stopRecordTimer()
    this.state.phase = 'idle'
    this.setCaption()
    try {
      await this.streamService.items?.recorder.stopRecord()
    } catch {
      /* nothing to clean up */
    }
  }

  private async sendRecording(auto: boolean) {
    if (this.state.phase !== 'rec') return
    this.stopRecordTimer()
    const durationSec = Math.min(
      MAX_RECORD_SEC,
      (performance.now() - this.recStart) / 1000,
    )
    const recorder = this.streamService.items?.recorder
    const blob = recorder ? await recorder.stopRecord() : undefined
    if (this.recCancelled) return

    if (durationSec < MIN_RECORD_SEC || !blob || blob.size === 0) {
      this.state.phase = 'idle'
      this.setCaption()
      this.flash('Не расслышал, попробуй ещё раз')
      return
    }

    const m = this.addMsg({
      who: 'me',
      durationSec,
      blob,
      blobUrl: URL.createObjectURL(blob),
    })
    if (auto) this.showToast('Прошла минута — отправил запись сам')
    await this.submitTurn(m.id)
  }

  /** ER-05: re-send a turn whose upload failed. */
  resend(id: number) {
    const m = this.find(id)
    if (!m || m.status !== 'failed' || this.state.phase === 'think') return
    void this.submitTurn(id)
  }

  /** ER-03: drop the unrecognized turn and record again. */
  rerecord(id: number) {
    this.state.msgs = this.state.msgs.filter((m) => m.id !== id)
    void this.startRecording()
  }

  /** ER-04: retry after the model did not answer. The last learner turn is re-sent. */
  retry(id: number) {
    this.state.msgs = this.state.msgs.filter((m) => m.id !== id)
    const last = [...this.state.msgs]
      .reverse()
      .find((m) => m.who === 'me' && !m.kind)
    if (last) void this.submitTurn(last.id)
  }

  private async submitTurn(id: number) {
    const m = this.find(id)
    if (!m?.blob) return
    const gen = this.gen
    this.state.phase = 'think'
    this.setCaption()
    this.state.msgs = this.state.msgs.filter((x) => x.kind !== 'error')
    m.status = 'ok'
    this.addMsg({ who: 'ai', kind: 'typing' })

    // Learner turns before this one, the model sees them as text.
    const history = this.history().filter(
      (_, i, arr) => i < arr.length - 1 || !m.text,
    )

    this.turnAbort?.abort()
    this.turnAbort = new AbortController()
    let res
    try {
      res = await this.api.turn(
        m.blob,
        this.state.role.id,
        history,
        this.turnAbort.signal,
      )
    } catch (e) {
      if (gen !== this.gen) return
      this.removeTyping()
      this.state.phase = 'idle'
      this.setCaption()
      const offline = !navigator.onLine || e instanceof TypeError
      if (offline) {
        m.status = 'failed'
      } else {
        this.addMsg({ who: 'ai', kind: 'error' })
      }
      return
    }
    if (gen !== this.gen) return
    this.removeTyping()

    if (res.status === 'unrecognized') {
      m.status = 'unrec'
      this.state.phase = 'idle'
      this.setCaption()
      return
    }

    m.text = res.transcript
    m.status = 'ok'
    this.countSent(m)
    const reply = this.addMsg({
      who: 'ai',
      text: res.reply,
      translation: res.reply_translation,
      audioUrl: res.audio_url,
      durationSec: estimateDuration(res.reply, this.state.level),
    })
    this.state.phase = 'idle'
    this.setCaption()
    void this.play(reply.id, true)
  }

  private countSent(m: Message) {
    this.state.sentToday++
    this.state.secondsToday += m.durationSec
    if (this.state.sentToday === SHORT_TALK_TURNS) {
      this.showToast('Шаг дня «Разговор» засчитан')
    }
  }

  // -- playback ------------------------------------------------------------------------------------------------------

  async play(id: number, auto: boolean) {
    const m = this.find(id)
    if (!m || m.kind) return
    if (this.state.playingId === id && !auto) {
      this.stopPlayback()
      return
    }
    if (this.state.phase === 'rec') return
    this.stopPlayback()
    this.state.playingId = id
    this.state.playProgress = 0
    if (m.who === 'ai' && this.state.phase !== 'think') {
      this.state.phase = 'speak'
      this.setCaption()
    }

    const src = m.who === 'ai' ? m.audioUrl : m.blobUrl
    if (src) {
      await this.playAudio(m, id, src)
    } else if (m.who === 'ai' && m.text) {
      this.speakFallback(m, id)
    } else {
      this.endPlayback(id)
    }
  }

  private async playAudio(m: Message, id: number, src: string) {
    const audio = new Audio(src)
    this.audio = audio
    audio.onended = () => {
      this.endPlayback(id)
    }
    audio.onerror = () => {
      this.endPlayback(id)
    }
    const tick = () => {
      if (this.audio !== audio) return
      if (audio.duration && isFinite(audio.duration)) {
        this.state.playProgress = Math.min(
          1,
          audio.currentTime / audio.duration,
        )
        m.durationSec = audio.duration
      }
      this.playRaf = requestAnimationFrame(tick)
    }
    tick()
    try {
      await audio.play()
    } catch {
      // ER-08: autoplay blocked, the message keeps its play button.
      this.endPlayback(id)
    }
  }

  /** Browser speech synthesis when the server returned no audio (mock mode or TTS outage). */
  private speakFallback(m: Message, id: number) {
    if (!('speechSynthesis' in window)) {
      this.endPlayback(id)
      return
    }
    speechSynthesis.cancel()
    const u = new SpeechSynthesisUtterance(m.text)
    u.lang = 'en-US'
    u.rate = this.state.level === 'A1' || this.state.level === 'A2' ? 0.9 : 1
    const voice =
      speechSynthesis.getVoices().find((v) => /^en[-_]US/i.test(v.lang)) ?? null
    if (voice) u.voice = voice
    const start = performance.now()
    const tick = () => {
      if (this.utterance !== u) return
      this.state.playProgress = Math.min(
        0.98,
        (performance.now() - start) / 1000 / m.durationSec,
      )
      this.playRaf = requestAnimationFrame(tick)
    }
    u.onend = () => {
      this.endPlayback(id)
    }
    u.onerror = () => {
      this.endPlayback(id)
    }
    this.utterance = u
    tick()
    speechSynthesis.speak(u)
  }

  private endPlayback(id: number) {
    if (this.state.playingId !== id) return
    this.stopPlayback()
  }

  stopPlayback() {
    if (this.playRaf) cancelAnimationFrame(this.playRaf)
    this.playRaf = undefined
    if (this.audio) {
      this.audio.onended = null
      this.audio.onerror = null
      this.audio.pause()
      this.audio = undefined
    }
    if (this.utterance) {
      this.utterance.onend = null
      this.utterance.onerror = null
      this.utterance = undefined
      try {
        speechSynthesis.cancel()
      } catch {
        /* no synthesis */
      }
    }
    this.state.playingId = null
    this.state.playProgress = 0
    if (this.state.phase === 'speak') {
      this.state.phase = 'idle'
      this.setCaption()
    }
  }

  /** ER-06: tab hidden, call, lock screen. */
  onHidden() {
    if (this.state.screen !== 'talk') return
    if (this.state.phase === 'rec') void this.cancelRecording()
    this.stopPlayback()
  }

  // -- summary -------------------------------------------------------------------------------------------------------

  openSummary() {
    this.gen++
    if (this.state.phase === 'rec') void this.cancelRecording()
    this.stopPlayback()
    this.turnAbort?.abort()
    this.removeTyping()
    this.state.msgs = this.state.msgs.filter((x) => x.kind !== 'error')
    this.state.phase = 'idle'
    this.state.roleOpen = false
    this.state.hintOpen = false
    this.state.screen = 'summary'

    const part = this.state.msgs
      .slice(this.state.summaryFrom)
      .filter((m) => !m.kind)
    const mine = part.filter(
      (m) => m.who === 'me' && m.status === 'ok' && m.text,
    )
    const speechSeconds = Math.round(
      mine.reduce((a, m) => a + m.durationSec, 0),
    )

    this.state.summary = {
      ...emptySummary(),
      speechSeconds,
      userTurns: mine.length,
      short: mine.length < SHORT_TALK_TURNS,
      loading: mine.length >= SHORT_TALK_TURNS,
    }
    if (this.state.summary.short) return
    void this.loadSummary(part)
  }

  retrySummary() {
    const part = this.state.msgs
      .slice(this.state.summaryFrom)
      .filter((m) => !m.kind)
    this.state.summary.loading = true
    this.state.summary.error = false
    void this.loadSummary(part)
  }

  private async loadSummary(part: Message[]) {
    const gen = this.gen
    this.summaryAbort?.abort()
    this.summaryAbort = new AbortController()
    const turns = part
      .filter((m) => m.status === 'ok' && m.text)
      .map((m) => ({
        role: m.who === 'ai' ? ('ai' as const) : ('user' as const),
        text: m.text,
        duration_ms: Math.round(m.durationSec * 1000),
      }))
    try {
      const res = await this.api.summary(
        turns,
        this.state.vocab,
        this.summaryAbort.signal,
      )
      if (gen !== this.gen) return
      this.state.summary.mistakes = res.mistakes
      this.state.summary.words = res.words.filter(
        (w) => !this.state.vocab.includes(w.word),
      )
      this.state.summary.loading = false
    } catch {
      if (gen !== this.gen) return
      this.state.summary.loading = false
      this.state.summary.error = true
    }
  }

  continueTalk() {
    if (!this.state.summary.short) {
      this.state.summaryFrom = this.state.msgs.length
    }
    this.state.screen = 'talk'
    this.state.phase = 'idle'
    this.setCaption()
  }

  toggleWord(word: string) {
    const i = this.state.vocab.indexOf(word)
    if (i >= 0) this.state.vocab.splice(i, 1)
    else this.state.vocab.push(word)
  }

  addAllWords() {
    this.state.summary.words.forEach((w) => {
      if (!this.state.vocab.includes(w.word)) this.state.vocab.push(w.word)
    })
    this.showToast('Слова добавлены в Словарик')
  }

  playUrl(url: string | null, text: string) {
    this.stopPlayback()
    if (url) {
      const audio = new Audio(url)
      this.audio = audio
      void audio.play().catch(() => undefined)
    } else if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(text)
      u.lang = 'en-US'
      speechSynthesis.speak(u)
    }
  }
}

function normalizeSpeech(text: string) {
  return text
    .toLowerCase()
    .replace(/’/g, "'")
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Rough spoken length until the real audio duration is known. */
export function estimateDuration(text: string, level: string) {
  const rate = level === 'A1' || level === 'A2' ? 0.9 : 1
  return Math.max(1.6, text.split(/\s+/).length / (2.6 * rate) + 0.4)
}

export function formatClock(sec: number) {
  const s = Math.max(0, Math.round(sec))
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0')
}

export function plural(n: number, forms: [string, string, string]) {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return forms[2]
  if (b > 1 && b < 5) return forms[1]
  if (b === 1) return forms[0]
  return forms[2]
}

export function speechLabel(sec: number) {
  const s = Math.round(sec)
  if (s < 60) return s + ' с'
  const m = Math.floor(s / 60)
  const r = s % 60
  return m + ' мин' + (r ? ' ' + r + ' с' : '')
}
